import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminSkeletonComponent,
} from '../../../shared/admin';
import type {
  DashboardFunnelDateRange,
  DashboardFunnelFilters,
  FunnelAnalytics,
} from '../../../shared/models/funnel-analytics.models';
import type { AssignablePlatformUserDto } from '../../../shared/services/platform-users.service';
import { PlatformDashboardService } from '../../../shared/services/platform-dashboard.service';
import {
  buildFunnelStageViewModels,
  buildFunnelTooltip,
  formatFunnelCount,
  formatFunnelCountWithRate,
  formatFunnelRate,
  funnelSegmentClipPath,
  funnelStageColor,
  type FunnelTooltipLines,
} from './dashboard-funnel-visual.util';

@Component({
  selector: 'app-dashboard-funnel-card',
  standalone: true,
  imports: [FormsModule, AdminEmptyStateComponent, AdminIconComponent, AdminSkeletonComponent],
  templateUrl: './dashboard-funnel-card.component.html',
  styleUrl: './dashboard-funnel-card.component.scss',
  host: {
    class: 'dashboard-funnel-card-host',
  },
})
export class DashboardFunnelCardComponent implements OnInit {
  private readonly dashboardService = inject(PlatformDashboardService);

  readonly title = input.required<string>();
  readonly entity = input.required<'leads' | 'applicants'>();
  readonly owners = input<AssignablePlatformUserDto[]>([]);
  readonly analyticsLoaded = output<FunnelAnalytics | null>();

  readonly analytics = signal<FunnelAnalytics | null>(null);
  readonly loading = signal(true);
  readonly refreshing = signal(false);
  readonly error = signal<string | null>(null);

  readonly dateRange = signal<DashboardFunnelDateRange>('last30Days');
  readonly createdFrom = signal('');
  readonly createdTo = signal('');
  readonly ownerUserId = signal('');

  readonly stageViews = computed(() => buildFunnelStageViewModels(this.analytics()?.stages ?? []));
  readonly totalCount = computed(() =>
    (this.analytics()?.stages ?? []).reduce((sum, stage) => sum + stage.count, 0),
  );
  readonly totalLabel = computed(() =>
    this.entity() === 'leads' ? 'Total Leads' : 'Total Applicants',
  );
  readonly footerLabel = computed(() =>
    this.entity() === 'leads' ? 'Overall Lead Conversion Rate' : 'Overall Applicant Conversion Rate',
  );
  readonly infoText = computed(() =>
    this.entity() === 'leads'
      ? 'Lead funnel stages are generated from your configured lead statuses.'
      : 'Applicant funnel stages are generated from your configured applicant statuses.',
  );

  constructor() {
    effect(() => {
      this.analyticsLoaded.emit(this.analytics());
    });
  }

  ngOnInit() {
    this.load();
  }

  onDateRangeChange(value: DashboardFunnelDateRange) {
    this.dateRange.set(value);
    this.load();
  }

  onCustomDateChange() {
    if (this.dateRange() === 'custom') {
      this.load();
    }
  }

  onOwnerChange(value: string) {
    this.ownerUserId.set(value);
    this.load();
  }

  stageColor(index: number): string {
    return funnelStageColor(index);
  }

  tooltipFor(stage: ReturnType<typeof buildFunnelStageViewModels>[number]): FunnelTooltipLines {
    return buildFunnelTooltip(stage);
  }

  formatCount = formatFunnelCount;
  formatRate = formatFunnelRate;
  formatCountWithRate = formatFunnelCountWithRate;
  segmentClipPath = funnelSegmentClipPath;

  private load() {
    const isInitialLoad = !this.analytics();
    if (isInitialLoad) {
      this.loading.set(true);
    } else {
      this.refreshing.set(true);
    }
    this.error.set(null);

    const filters: DashboardFunnelFilters = {
      dateRange: this.dateRange(),
      createdFrom: this.dateRange() === 'custom' ? this.createdFrom() || undefined : undefined,
      createdTo: this.dateRange() === 'custom' ? this.createdTo() || undefined : undefined,
      ownerUserId: this.ownerUserId() || undefined,
    };

    const request$ =
      this.entity() === 'leads'
        ? this.dashboardService.getLeadFunnel(filters)
        : this.dashboardService.getApplicantFunnel(filters);

    request$.subscribe({
      next: (data) => {
        this.analytics.set(data);
        this.loading.set(false);
        this.refreshing.set(false);
      },
      error: () => {
        this.analytics.set(null);
        this.error.set('Unable to load funnel analytics.');
        this.loading.set(false);
        this.refreshing.set(false);
      },
    });
  }
}
